import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldBan, Trash2, Plus, Mail, Phone, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlockList() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('email');
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/blocklist`, { withCredentials: true });
      setEntries(res.data || []);
    } catch {
      toast.error('Failed to load block list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addEntry = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/blocklist`, { type, value: value.trim() }, { withCredentials: true });
      toast.success(res.data.flagged ? `Blocked. ${res.data.flagged} existing lead(s) moved to Spam.` : 'Added to block list.');
      setValue('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Unblock ${entry.value}? They'll be able to submit forms again.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blocklist`, {
        params: { type: entry.type, value: entry.value }, withCredentials: true,
      });
      toast.success('Unblocked');
      setEntries((prev) => prev.filter((x) => !(x.type === entry.type && x.value === entry.value)));
    } catch {
      toast.error('Failed to unblock');
    }
  };

  return (
    <AdminLayout title="Block List">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-blocklist-page">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 rounded-xl bg-[#DC2626] text-white flex items-center justify-center"><ShieldBan size={20} /></span>
          <div>
            <h1 className="text-2xl font-black text-[#0A0F1C] tracking-tight">Block List</h1>
            <p className="text-sm text-[#64748B]">Blocked emails and phone numbers are auto-flagged as spam on every future submission.</p>
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={addEntry} className="mt-5 bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="blocklist-add-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-[#64748B] mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="blocklist-type-select"
              className="px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm bg-white focus:border-[#1D4ED8] outline-none">
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-bold uppercase tracking-wide text-[#64748B] mb-1">{type === 'email' ? 'Email address' : 'Phone number'}</label>
            <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="blocklist-value-input"
              placeholder={type === 'email' ? 'spammer@example.com' : '8054002077'}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm focus:border-[#1D4ED8] outline-none" />
          </div>
          <button type="submit" disabled={adding} data-testid="blocklist-add-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#DC2626] text-white font-bold text-sm hover:bg-[#B91C1C] disabled:opacity-60">
            {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Block
          </button>
        </form>

        {/* List */}
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F1F5F9] text-sm font-bold text-[#334155]">
            Blocked ({entries.length})
          </div>
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#DC2626]" size={22} /></div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]" data-testid="blocklist-empty">
              Nothing blocked yet. Use the form above, or hit “Block &amp; mark spam” on any lead.
            </div>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]" data-testid="blocklist-items">
              {entries.map((e) => (
                <li key={`${e.type}:${e.value}`} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`blocklist-item-${e.value}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${e.type === 'email' ? 'bg-blue-50 text-[#1D4ED8]' : 'bg-emerald-50 text-emerald-600'}`}>
                      {e.type === 'email' ? <Mail size={15} /> : <Phone size={15} />}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[#0A0F1C] truncate">{e.value}</div>
                      <div className="text-xs text-[#94A3B8]">{e.type} · blocked {e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}</div>
                    </div>
                  </div>
                  <button onClick={() => removeEntry(e)} data-testid={`blocklist-remove-${e.value}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#64748B] hover:text-[#DC2626] px-2 py-1 rounded-md">
                    <Trash2 size={15} /> Unblock
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
